import { readFile, readdir, stat, writeFile } from "node:fs/promises";
import path from "node:path";

import { parsePrimeUiExportManifest } from "../lib/api-v1-contract.js";
import type {
  PrimeUiDependencySection,
  PrimeUiDependencyToAdd,
  PrimeUiDependencyVersionConflict,
  PrimeUiExportManifest,
} from "../types.js";

const DEPENDENCY_SECTIONS: PrimeUiDependencySection[] = [
  "dependencies",
  "devDependencies",
  "peerDependencies",
];

export type PackageJsonRecord = Record<string, unknown> & {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  peerDependencies?: Record<string, string>;
};

type DependencyMatch = {
  section: PrimeUiDependencySection;
  version: string;
};

export function toPosixPath(value: string): string {
  return value.split(path.sep).join("/");
}

export function toProjectRelative(
  rootPath: string,
  absolutePath: string,
): string {
  return toPosixPath(path.relative(rootPath, absolutePath));
}

export function isBinaryBuffer(buffer: Buffer): boolean {
  const limit = Math.min(buffer.length, 8000);
  for (let index = 0; index < limit; index += 1) {
    if (buffer[index] === 0) {
      return true;
    }
  }
  return false;
}

export async function readJsonFile<T>(filePath: string): Promise<T> {
  const content = await readFile(filePath, "utf-8");
  return JSON.parse(content) as T;
}

export async function fileExists(filePath: string): Promise<boolean> {
  try {
    await stat(filePath);
    return true;
  } catch {
    return false;
  }
}

export function parseManifest(value: unknown): PrimeUiExportManifest {
  try {
    return parsePrimeUiExportManifest(value);
  } catch (error) {
    if (
      error instanceof Error &&
      error.message === "Export manifest is invalid."
    ) {
      throw new Error("Invalid export manifest format.");
    }

    throw error;
  }
}

export async function resolveManifestCandidateFiles(input: {
  exportPath: string;
  manifestOwnerLabel: string;
  files: string[];
  requireExisting?: boolean;
}): Promise<string[]> {
  const result = new Set<string>();
  const requireExisting = input.requireExisting ?? true;

  for (const rawPath of input.files) {
    const trimmed = rawPath.trim();
    if (!trimmed) {
      throw new Error(
        `Export manifest for ${input.manifestOwnerLabel} contains an empty file path.`,
      );
    }

    const normalizedRelative = toPosixPath(trimmed).replace(/^\.\/+/, "");
    const absolutePath = path.resolve(input.exportPath, normalizedRelative);
    const relative = path.relative(input.exportPath, absolutePath);
    if (relative.startsWith("..") || path.isAbsolute(relative)) {
      throw new Error(
        `Export manifest for ${input.manifestOwnerLabel} contains path outside export root: ${trimmed}`,
      );
    }

    if (requireExisting) {
      const stats = await stat(absolutePath).catch(() => null);
      if (!stats?.isFile()) {
        throw new Error(
          `Export manifest file is missing for ${input.manifestOwnerLabel}: ${normalizedRelative}`,
        );
      }
    }

    result.add(absolutePath);
  }

  return [...result].sort((a, b) => a.localeCompare(b));
}

export async function resolveSingleExportDirectory(
  exportsRoot: string,
): Promise<{
  exportId: string;
  exportPath: string;
}> {
  const entries = await readdir(exportsRoot, { withFileTypes: true });
  const exportDirectories = entries
    .filter((entry) => entry.isDirectory())
    .map((entry) => entry.name)
    .sort((a, b) => a.localeCompare(b));

  if (exportDirectories.length === 0) {
    throw new Error(
      "No downloaded exports found in .primeui/temp/exports. Run download_export first.",
    );
  }

  if (exportDirectories.length > 1) {
    throw new Error(
      "Multiple export folders found in .primeui/temp/exports. Clear temp with clear_temp and download one export.",
    );
  }

  const exportId = exportDirectories[0] ?? "";
  return {
    exportId,
    exportPath: path.join(exportsRoot, exportId),
  };
}

export function createCopyId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ensureSafeTargetPath(
  projectRoot: string,
  targetPath: string,
): void {
  const relative = path.relative(projectRoot, targetPath);
  if (relative.startsWith("..") || path.isAbsolute(relative)) {
    throw new Error(
      `Refusing to write outside project root. Computed target: ${targetPath}`,
    );
  }
}

export function getDependencyVersion(
  packageJson: PackageJsonRecord,
  packageName: string,
): DependencyMatch | null {
  for (const section of DEPENDENCY_SECTIONS) {
    const sectionData = packageJson[section];
    if (!sectionData || typeof sectionData !== "object") {
      continue;
    }
    const value = (sectionData as Record<string, unknown>)[packageName];
    if (typeof value === "string") {
      return {
        section,
        version: value,
      };
    }
  }

  return null;
}

export function getOrCreateDependencySection(
  packageJson: PackageJsonRecord,
  section: PrimeUiDependencySection,
): Record<string, string> {
  const existing = packageJson[section];
  if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
    const created: Record<string, string> = {};
    packageJson[section] = created;
    return created;
  }

  return existing as Record<string, string>;
}

export function sortDependencySections(packageJson: PackageJsonRecord): void {
  for (const section of DEPENDENCY_SECTIONS) {
    const sectionData = packageJson[section];
    if (
      !sectionData ||
      typeof sectionData !== "object" ||
      Array.isArray(sectionData)
    ) {
      continue;
    }

    const sorted = Object.fromEntries(
      Object.entries(sectionData as Record<string, string>).sort(([a], [b]) =>
        a.localeCompare(b),
      ),
    );
    packageJson[section] = sorted;
  }
}

export async function resolveDependencyRequirements(input: {
  exportPackageJsonPath: string;
  userPackageJsonPath: string;
  requiredPackageNames: Iterable<string>;
}): Promise<{
  missedDependencies: PrimeUiDependencyToAdd[];
  dependenciesVersionConflicts: PrimeUiDependencyVersionConflict[];
}> {
  const exportPackageJson = await readJsonFile<PackageJsonRecord>(
    input.exportPackageJsonPath,
  );
  const userPackageJson = await readJsonFile<PackageJsonRecord>(
    input.userPackageJsonPath,
  );

  const missedDependencies: PrimeUiDependencyToAdd[] = [];
  const dependenciesVersionConflicts: PrimeUiDependencyVersionConflict[] = [];

  for (const packageName of new Set(input.requiredPackageNames)) {
    const exportDependency = getDependencyVersion(
      exportPackageJson,
      packageName,
    );
    if (!exportDependency) {
      continue;
    }

    const userDependency = getDependencyVersion(userPackageJson, packageName);
    if (!userDependency) {
      missedDependencies.push({
        packageName,
        version: exportDependency.version,
        section: exportDependency.section,
      });
      continue;
    }

    if (userDependency.version !== exportDependency.version) {
      dependenciesVersionConflicts.push({
        packageName,
        section: exportDependency.section,
        exportVersion: exportDependency.version,
        userVersion: userDependency.version,
      });
    }
  }

  return {
    missedDependencies: missedDependencies.sort((a, b) =>
      a.packageName.localeCompare(b.packageName),
    ),
    dependenciesVersionConflicts: dependenciesVersionConflicts.sort((a, b) =>
      a.packageName.localeCompare(b.packageName),
    ),
  };
}

export async function applyDependencyRequirementsToPackageJson(input: {
  exportPackageJsonPath: string;
  userPackageJsonPath: string;
  requiredPackageNames: Iterable<string>;
}): Promise<{
  addedDependencies: PrimeUiDependencyToAdd[];
  dependenciesVersionConflicts: PrimeUiDependencyVersionConflict[];
}> {
  const exportPackageJson = await readJsonFile<PackageJsonRecord>(
    input.exportPackageJsonPath,
  );
  const userPackageJson = await readJsonFile<PackageJsonRecord>(
    input.userPackageJsonPath,
  );

  const addedDependencies: PrimeUiDependencyToAdd[] = [];
  const dependenciesVersionConflicts: PrimeUiDependencyVersionConflict[] = [];

  for (const packageName of new Set(input.requiredPackageNames)) {
    const exportDependency = getDependencyVersion(
      exportPackageJson,
      packageName,
    );
    if (!exportDependency) {
      continue;
    }

    const userDependency = getDependencyVersion(userPackageJson, packageName);
    if (!userDependency) {
      const sectionData = getOrCreateDependencySection(
        userPackageJson,
        exportDependency.section,
      );
      sectionData[packageName] = exportDependency.version;
      addedDependencies.push({
        packageName,
        version: exportDependency.version,
        section: exportDependency.section,
      });
      continue;
    }

    if (userDependency.version !== exportDependency.version) {
      dependenciesVersionConflicts.push({
        packageName,
        section: exportDependency.section,
        exportVersion: exportDependency.version,
        userVersion: userDependency.version,
      });
    }
  }

  if (addedDependencies.length > 0) {
    sortDependencySections(userPackageJson);
    await writeFile(
      input.userPackageJsonPath,
      `${JSON.stringify(userPackageJson, null, 2)}\n`,
      "utf-8",
    );
  }

  return {
    addedDependencies: addedDependencies.sort((a, b) =>
      a.packageName.localeCompare(b.packageName),
    ),
    dependenciesVersionConflicts: dependenciesVersionConflicts.sort((a, b) =>
      a.packageName.localeCompare(b.packageName),
    ),
  };
}
